import { useBasket } from '../../context/BasketContext';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Plus, Minus, Trash2, ShoppingCart, Check, RefreshCw, Tag } from 'lucide-react';

export const Basket = ({ onCompleteSale, loading, vatRate, discount, setDiscount }) => {
  const { items, groupedItems, total, incrementItem, decrementItem, removeItem, clearBasket } = useBasket();
  
  const subtotal = items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
  const vatAmount = subtotal * (vatRate / 100);
  
  let discountAmount = 0;
  if (discount) {
    if (discount.type === 'percentage') {
      discountAmount = subtotal * (discount.value / 100);
    } else {
      discountAmount = discount.value;
    }
  }
  
  const finalTotal = subtotal + vatAmount - discountAmount;

  return (
    <div className="basket-container bg-white">
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-slate-900">Order</h2>
          <div className="flex items-center gap-2">
            {discount && (
              <Button variant="ghost" size="sm" onClick={() => setDiscount(null)} className="text-green-600">
                <Tag className="w-4 h-4 mr-1" />
                {discount.type === 'percentage' ? `${discount.value}%` : `£${discount.value}`}
              </Button>
            )}
            {items.length > 0 && (
              <Button variant="ghost" size="sm" data-testid="clear-basket" onClick={clearBasket}
                className="text-slate-500 hover:text-red-500">
                <Trash2 className="w-4 h-4 mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      <ScrollArea className="basket-items">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-400">
            <ShoppingCart className="w-12 h-12 mb-3" />
            <p className="font-body">No items in basket</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {groupedItems.map(({ parent, addons }) => (
              <div key={parent.item_id}>
                <div className="basket-item" data-testid={`basket-item-${parent.item_id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-body font-medium text-slate-800 truncate">{parent.product_name}</p>
                    <p className="text-sm text-slate-500">£{parent.unit_price_inc_vat.toFixed(2)} each</p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button variant="outline" size="icon" data-testid={`decrement-${parent.item_id}`}
                      onClick={() => decrementItem(parent.item_id)} className="w-9 h-9 touch-press">
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="w-8 text-center font-heading font-bold text-lg">{parent.quantity}</span>
                    <Button variant="outline" size="icon" data-testid={`increment-${parent.item_id}`}
                      onClick={() => incrementItem(parent.item_id)} className="w-9 h-9 touch-press">
                      <Plus className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" data-testid={`remove-${parent.item_id}`}
                      onClick={() => removeItem(parent.item_id)} className="w-9 h-9 text-slate-400 hover:text-red-500 touch-press">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {addons.map(addon => (
                  <div key={addon.item_id}
                    className="basket-item pl-6 bg-amber-50/50 border-l-2 border-l-amber-300 ml-2"
                    data-testid={`basket-addon-${addon.item_id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-sm text-amber-800 truncate">+ {addon.product_name}</p>
                      <p className="text-xs text-amber-600">+£{addon.unit_price_inc_vat.toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button variant="outline" size="icon" onClick={() => decrementItem(addon.item_id)} className="w-7 h-7 touch-press">
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="w-6 text-center font-heading font-bold text-sm">{addon.quantity}</span>
                      <Button variant="outline" size="icon" onClick={() => incrementItem(addon.item_id)} className="w-7 h-7 touch-press">
                        <Plus className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => removeItem(addon.item_id)}
                        className="w-7 h-7 text-slate-400 hover:text-red-500 touch-press">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      <div className="basket-footer">
        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-sm text-slate-600">
            <span>Subtotal</span>
            <span>£{subtotal.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm text-slate-600">
            <span>VAT ({vatRate}%)</span>
            <span>£{vatAmount.toFixed(2)}</span>
          </div>
          {discount && (
            <div className="flex justify-between text-sm text-green-600">
              <span>Discount</span>
              <span>-£{discountAmount.toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between items-center pt-2 border-t">
            <span className="font-body text-lg text-slate-600">Total</span>
            <span className="price-display text-3xl text-slate-900">£{finalTotal.toFixed(2)}</span>
          </div>
        </div>
        <Button data-testid="complete-sale-btn"
          onClick={() => onCompleteSale(subtotal, vatAmount, finalTotal, discountAmount)}
          disabled={items.length === 0 || loading}
          className="w-full h-16 text-xl font-bold bg-blue-600 hover:bg-blue-700 touch-press">
          {loading ? <RefreshCw className="w-6 h-6 mr-2 animate-spin" /> : <Check className="w-6 h-6 mr-2" />}
          {loading ? 'Processing...' : 'Complete Sale'}
        </Button>
      </div>
    </div>
  );
};
